import React, {useState, useEffect, useMemo, useRef} from 'react';
import api from '../services/api';
import '../styles/ProjectDetails.css';
import {formatDate, weekdayName} from "../utils/dateConverter";
import CustomReportModal from '../modals/CustomReportModal';
import ServiceReports from './ServiceReports';
import ProjectPeriodSummary from './ProjectPeriodSummary';
import { getProjectHoursSummary } from '../services/OrderService';
import { getTicketsByProject } from '../services/ProjectTicketService';
import {
    listCustomReports, getCustomReport, saveCustomReport, updateCustomReport, deleteCustomReport,
} from '../services/CustomReportService';
import { useToast, useConfirm } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';
import { filenameFromResponse } from '../utils/contentDisposition';
import { rowProjectMinutes } from '../utils/reportTime';
import { monthRange, summarisePeriod } from '../utils/projectPeriodStats';

// German day names indexed by Date.getDay(). Only projectTime may be summed: two tickets on one
// day share the day span, so it is not a duration.
function mapEntry(e) {
    return {
        id: e.id,
        date: e.date,
        dayOfWeek: weekdayName(e.date),
        // start/end/break/travel are needed by the edit modal (camelCase).
        startTime: (e.start_time || '').slice(0, 5),
        endTime: (e.end_time || '').slice(0, 5),
        breakMinutes: e.break_minutes ?? 0,
        travelMinutes: e.travel_minutes ?? 0,
        projectTime: e.work_time || '',
        // Always empty in the new schema; kept for forward-compatibility, never rendered.
        location: e.location || '',
        ticketTitle: e.ticket_title || '',
        activity: e.activity,
        comment: e.comment,
    };
}

function mapProjectStats(raw) {
    return raw.map(u => ({
        userId: u.user_id,
        username: u.username,
        name: u.name,
        // total_work_time: this user's project allocations in the range, "HH:MM".
        totalWorkTime: u.total_work_time,
        entries: Array.isArray(u.entries) ? u.entries.map(mapEntry) : [],
    }));
}

export default function ProjectDetails({project}) {
    const toast = useToast();
    const confirm = useConfirm();

    const [dateRange, setDateRange] = useState(() => monthRange());

    // `range` is the one `rows` belong to, so the summary never labels figures with a newer range.
    const [stats, setStats] = useState({status: 'loading', range: dateRange, rows: null});
    const statsRequest = useRef(0);
    const projectStats = useMemo(() => (stats.rows ? mapProjectStats(stats.rows) : null), [stats.rows]);
    const periodSummary = useMemo(() => (stats.rows ? summarisePeriod(stats.rows) : null), [stats.rows]);
    const [hoursSummary, setHoursSummary] = useState(null);

    const [editProjectReportModalOpen, setEditProjectReportModalOpen] = useState(false);

    // editingReport null = a fresh report seeded from the live stats.
    const [savedReports, setSavedReports] = useState([]);
    const [editingReport, setEditingReport] = useState(null);

    const [projectTickets, setProjectTickets] = useState([]);

    // POST, not GET: the date-range filter travels in the request body. Numbered so an answer
    // for an older range cannot land after the newer one.
    const loadProjectStats = () => {
        const range = {...dateRange};
        const request = ++statsRequest.current;
        if (!range.startDate || !range.endDate) {
            setStats({status: 'incomplete', range, rows: null});
            toast("Please select start and end date.");
            return;
        }
        if (range.startDate > range.endDate) {
            setStats({status: 'reversed', range, rows: null});
            return;
        }

        api.post(`/Project/projectStats/${project.id}`, {
            start_date: range.startDate,
            end_date: range.endDate
        })
            .then(res => {
                if (request === statsRequest.current) setStats({status: 'done', range, rows: res.data});
            })
            .catch(err => {
                if (request !== statsRequest.current) return;
                setStats({status: 'error', range, rows: null});
                toast(extractApiError(err, "Failed to load stats"));
            });
    };

    const generateReport = () => {
        api.post(
            `/Project/Report/${project.id}`,
            {start_date: dateRange.startDate, end_date: dateRange.endDate},
            {responseType: 'blob'}
        ).then(res => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            // The server names the archive <Projekt_Start_Ende>.zip; don't restate it here.
            link.setAttribute('download', filenameFromResponse(res, `${project.name}.zip`));
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }).catch(err => {
            toast(extractApiError(err, "Failed to generate report"));
        });
    };

    const handleSubmitCustomReport = async (bulkData, range = dateRange) => {
        if (!project?.id) {
            toast('Projekt-ID fehlt.');
            return;
        }

        try {
            const requestBody = {
                startDate: range.startDate,
                endDate: range.endDate,
                users: bulkData.map(block => ({
                    userId: block.userId,
                    entries: block.entries.map(e => {
                        // Sending the row's project time keeps a day split over two tickets from
                        // being counted twice; omitted when absent, so an older report keeps its totals.
                        const projectMinutes = rowProjectMinutes(e);
                        return {
                            userId: block.userId,
                            projectId: project.id,
                            date: e.date,                    // "yyyy-MM-dd"
                            startTime: e.startTime,          // "HH:mm"
                            endTime: e.endTime,              // "HH:mm"
                            breakMinutes: Number(e.breakMinutes) || 0,
                            travelMinutes: Number(e.travelMinutes) || 0,
                            kilometers: e.kilometers ?? 0,
                            location: e.location ?? null,
                            comment: e.comment ?? null,
                            activity: e.activity ?? null,
                            ticketTitle: e.ticketTitle ?? null,
                            ...(projectMinutes === null ? {} : {projectMinutes})
                        };
                    })
                }))
            };

            const res = await api.post(`/CustomReport/bulk/${project.id}`, requestBody, {
                responseType: 'blob'
            });

            const blob = new Blob([res.data], {type: 'application/zip'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filenameFromResponse(res, `${project.name}.zip`));
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (err) {
            console.error(err);
            toast(extractApiError(err, 'Fehler beim Speichern/Erstellen des Berichts.'));
        }
    };

    const loadSavedReports = () => {
        if (!project?.id) return;
        listCustomReports(project.id)
            .then(setSavedReports)
            .catch(() => setSavedReports([]));
    };

    const handleOpenNewReport = () => {
        setEditingReport(null);
        setEditProjectReportModalOpen(true);
    };

    const handleOpenSavedReport = async (id) => {
        try {
            const rep = await getCustomReport(id);
            setEditingReport(rep);
            if (rep.start_date && rep.end_date) {
                setDateRange({startDate: rep.start_date, endDate: rep.end_date});
            }
            setEditProjectReportModalOpen(true);
        } catch (err) {
            toast(extractApiError(err, 'Bericht konnte nicht geladen werden.'));
        }
    };

    // Only stores a snapshot; it never changes any employee's hours.
    const handleSaveCustomReport = async ({name, users}) => {
        const body = {name, startDate: dateRange.startDate, endDate: dateRange.endDate, users};
        try {
            if (editingReport?.id) {
                await updateCustomReport(editingReport.id, body);
                toast('Bericht aktualisiert.');
            } else {
                await saveCustomReport(project.id, body);
                toast('Bericht gespeichert.');
            }
            setEditProjectReportModalOpen(false);
            setEditingReport(null);
            loadSavedReports();
        } catch (err) {
            toast(extractApiError(err, 'Speichern des Berichts fehlgeschlagen.'));
        }
    };

    const handleDeleteSavedReport = async (rep) => {
        if (!await confirm(`Bericht "${rep.name}" wirklich löschen?`, {confirmLabel: 'Löschen', danger: true})) {
            return;
        }
        try {
            await deleteCustomReport(rep.id);
            loadSavedReports();
        } catch (err) {
            toast(extractApiError(err, 'Löschen fehlgeschlagen.'));
        }
    };

    const handleGenerateSavedPdf = async (rep) => {
        try {
            const full = await getCustomReport(rep.id);
            await handleSubmitCustomReport(full.users, {
                startDate: full.start_date || dateRange.startDate,
                endDate: full.end_date || dateRange.endDate,
            });
        } catch (err) {
            toast(extractApiError(err, 'PDF konnte nicht erstellt werden.'));
        }
    };

    useEffect(() => {
        if (project?.id) {
            setStats({status: 'loading', range: dateRange, rows: null});
            loadProjectStats();
            getProjectHoursSummary(project.id).then(setHoursSummary).catch(() => setHoursSummary(null));
        }
    }, [project, dateRange]);

    useEffect(() => {
        setSavedReports([]);
        loadSavedReports();
        if (project?.id) {
            getTicketsByProject(project.id).then(setProjectTickets).catch(() => setProjectTickets([]));
        } else {
            setProjectTickets([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project]);

    return (
        <div className="project-details-container">

            {hoursSummary && (
                <div className="pd-hours-card">
                    <h4 className="pd-hours-title">Stundenbudget</h4>
                    <div className="pd-hours-kpis">
                        <div className="pd-hours-kpi">
                            <span className="pd-hours-kpi-label">Beauftragt</span>
                            <span className="pd-hours-kpi-value">{hoursSummary.ordered_hours} h</span>
                        </div>
                        <div className="pd-hours-kpi">
                            <span className="pd-hours-kpi-label">Gebucht</span>
                            <span className="pd-hours-kpi-value">{hoursSummary.worked_hours} h</span>
                        </div>
                        <div className={`pd-hours-kpi${hoursSummary.free_hours < 0 ? ' pd-hours-kpi--warn' : ' pd-hours-kpi--ok'}`}>
                            <span className="pd-hours-kpi-label">Verfügbar</span>
                            <span className="pd-hours-kpi-value">{hoursSummary.free_hours} h</span>
                        </div>
                    </div>

                    {hoursSummary.ordered_hours > 0 && (
                        <div className="pd-hours-bar-wrap">
                            <div
                                className={`pd-hours-bar${hoursSummary.free_hours < 0 ? ' pd-hours-bar--over' : ''}`}
                                style={{
                                    width: `${Math.min(100, (hoursSummary.worked_hours / hoursSummary.ordered_hours) * 100)}%`,
                                }}
                            />
                        </div>
                    )}

                    {hoursSummary.tickets && hoursSummary.tickets.length > 0 && (
                        <table className="pd-hours-table">
                            <thead>
                                <tr>
                                    <th>Ticket</th>
                                    <th style={{ textAlign: 'right' }}>Beauftragt</th>
                                    <th style={{ textAlign: 'right' }}>Gebucht</th>
                                    <th style={{ textAlign: 'right' }}>Verfügbar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hoursSummary.tickets.map(t => (
                                    <tr key={t.ticket_id} className={t.free_hours < 0 ? 'pd-hours-row--warn' : ''}>
                                        <td>{t.ticket_name || t.ticket_id}</td>
                                        <td style={{ textAlign: 'right' }}>{t.ordered_hours} h</td>
                                        <td style={{ textAlign: 'right' }}>{t.worked_hours} h</td>
                                        <td style={{ textAlign: 'right' }}>
                                            {t.free_hours} h
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            <ServiceReports project={project} />

            <div className="date-range">
                <input
                    type="date"
                    aria-label="Von"
                    value={dateRange.startDate}
                    onChange={e => setDateRange({...dateRange, startDate: e.target.value})}
                />
                <input
                    type="date"
                    aria-label="Bis"
                    value={dateRange.endDate}
                    onChange={e => setDateRange({...dateRange, endDate: e.target.value})}
                />
                <button onClick={loadProjectStats} className="button">Load Stats</button>
                <button onClick={generateReport} className="button">Report</button>
                <button onClick={handleOpenNewReport} className="button">Custom Report</button>
            </div>

            <ProjectPeriodSummary status={stats.status} range={stats.range} summary={periodSummary} />

            {savedReports.length > 0 && (
                <div className="saved-reports">
                    <h4>Gespeicherte Berichte</h4>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Zeitraum</th>
                                <th>Zuletzt geändert</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {savedReports.map(rep => (
                                <tr key={rep.id}>
                                    <td>{rep.name}</td>
                                    <td>
                                        {rep.start_date && rep.end_date
                                            ? `${formatDate(rep.start_date)} – ${formatDate(rep.end_date)}`
                                            : '—'}
                                    </td>
                                    <td>{rep.updated_at ? formatDate(rep.updated_at) : '—'}</td>
                                    <td style={{whiteSpace: 'nowrap'}}>
                                        <button className="button" onClick={() => handleOpenSavedReport(rep.id)}>
                                            Bearbeiten
                                        </button>
                                        <button className="button" style={{marginLeft: '6px'}}
                                                onClick={() => handleGenerateSavedPdf(rep)}>
                                            PDF
                                        </button>
                                        <button className="button button-danger" style={{marginLeft: '6px'}}
                                                onClick={() => handleDeleteSavedReport(rep)}>
                                            Löschen
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {projectStats !== null && (
                <div className="project-stats">
                    {projectStats.length > 0 ? (
                        projectStats.map(user => (
                            <div key={user.userId} className="user-stat">
                                <h4>{user.username} — Gesamt: {user.totalWorkTime}</h4>

                                {/* Projektzeit is the time allocated to this project, not the length of the working day. */}
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Datum</th>
                                            <th>Wochentag</th>
                                            <th>Projektzeit</th>
                                            <th>Ticket</th>
                                            <th>Einsatzort</th>
                                            <th>Aktivität</th>
                                            <th>Kommentar</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {user.entries.map((entry, index) => (
                                            <tr key={index}>
                                                <td>{formatDate(entry.date)}</td>
                                                <td>{entry.dayOfWeek}</td>
                                                <td>{entry.projectTime}</td>
                                                <td>{entry.ticketTitle || '—'}</td>
                                                <td>{entry.location || '—'}</td>
                                                <td>{entry.activity || '—'}</td>
                                                <td>{entry.comment || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))
                    ) : (
                        <p>Keine Einträge in diesem Bereich gefunden.</p>
                    )}
                </div>
            )}

            {editProjectReportModalOpen && (
                <CustomReportModal
                    key={editingReport ? editingReport.id : 'new'}
                    users={editingReport ? editingReport.users : (projectStats || [])}
                    dateRange={dateRange}
                    tickets={projectTickets}
                    initialName={editingReport ? editingReport.name : ''}
                    onClose={() => { setEditProjectReportModalOpen(false); setEditingReport(null); }}
                    onSubmit={handleSubmitCustomReport}
                    onSave={handleSaveCustomReport}
                />
            )}
        </div>
    );
}